import React from 'react';
import type { Roadmap, RoadmapPhase, RoadmapAction } from '../types';

const PHASE_RULE: Record<RoadmapPhase['color'], string> = {
  blue:    'border-l-brand-500',
  purple:  'border-l-brand-navySoft',
  emerald: 'border-l-leaf-500',
};

const PHASE_TEXT: Record<RoadmapPhase['color'], string> = {
  blue:    'text-brand-700',
  purple:  'text-brand-navySoft',
  emerald: 'text-leaf-700',
};

const DIFFICULTY_LABEL: Record<RoadmapAction['difficulty'], { label: string; cls: string }> = {
  low:    { label: '易',  cls: 'text-leaf-700' },
  medium: { label: '中',  cls: 'text-amber-700' },
  high:   { label: '難',  cls: 'text-rose-700' },
};

const PhaseCard: React.FC<{ phase: RoadmapPhase }> = ({ phase }) => (
  <div className={`border-l-2 ${PHASE_RULE[phase.color]} pl-3`}>
    <div className="flex items-baseline justify-between mb-1 gap-2">
      <div>
        <span className={`text-[10px] font-semibold tracking-[0.08em] ${PHASE_TEXT[phase.color]}`}>
          PHASE {phase.number}
        </span>
        <span className="text-[10px] text-slate-500 ml-2">{phase.timeframe}</span>
      </div>
      <div className={`text-[11px] font-semibold tabular-nums ${PHASE_TEXT[phase.color]}`}>
        +{phase.expected_score_delta} pt
      </div>
    </div>
    <h4 className={`text-[13px] font-semibold mb-0.5 ${PHASE_TEXT[phase.color]}`}>{phase.title}</h4>
    <p className="text-[11px] text-slate-500 mb-2">{phase.subtitle}</p>

    {phase.actions.length === 0 ? (
      <div className="text-[11px] text-slate-400 italic">アクション項目なし</div>
    ) : (
      <ul className="space-y-1">
        {phase.actions.map((a, i) => {
          const diff = DIFFICULTY_LABEL[a.difficulty];
          return (
            <li key={i} className="flex items-start gap-2 text-[12px]">
              <span className="flex-1 text-slate-700">{a.text}</span>
              <span className={`shrink-0 text-[10px] ${diff.cls}`}>{diff.label}</span>
              <span className="shrink-0 text-[10px] text-slate-500 tabular-nums w-12 text-right">{a.impact}</span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const ImprovementRoadmap: React.FC<{ roadmap: Roadmap }> = ({ roadmap }) => {
  if (!roadmap || roadmap.phases.length === 0) return null;
  const totalDelta = roadmap.phases.reduce((sum, p) => sum + p.expected_score_delta, 0);

  return (
    <section className="py-4 border-b border-slate-200">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[13px] font-semibold text-slate-900">改善ロードマップ</h3>
        <span className="text-[11px] text-slate-500">
          受注後 6ヶ月、合計 <strong className="text-leaf-700">+{totalDelta} pt</strong> 目標
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
        {roadmap.phases.map((p) => <PhaseCard key={p.number} phase={p} />)}
      </div>
    </section>
  );
};

export default ImprovementRoadmap;
